import React from "react";
import Reveal from "../components/Reveal";
import ExploreInDepth from "../components/ExploreInDepth";
import { pillars } from "../data/site";
import { useImage } from "../lib/media";
import useSeo from "../lib/useSeo";
import { useT } from "../lib/i18n";

export default function About() {
  useSeo({
    title: "About Us | Yuva Ayurvedic Clinic Prayagraj | 18+ Years Experience",
    absoluteTitle: true,
    description: "Founded in Prayagraj in 2008, Yuva Ayurvedic Clinic Private Limited is a specialised Ayurvedic clinic for men's sexual health. 18+ years of experience, 50,000+ satisfied patients, 100% confidential Ayurvedic treatment with no side effects.",
    keywords: "About Yuva Ayurvedic, Ayurvedic doctor Prayagraj, Ayurvedic sexologist Prayagraj India, Confidential sexual health treatment India, Men's sexual health clinic India, NRI men's health treatment Prayagraj",
  });
  const t = useT();
  const aboutImg = useImage("about", "https://images.unsplash.com/photo-1775133163619-219dff93bf15?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80");
  return (
    <div className="pb-24" data-testid="about-page">
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-7">
            <div className="overline">{t("about.overline")}</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[0.95]">
              {t("about.title.l1")}<br/>{t("about.title.l2")}<br/><span className="italic text-terracotta">{t("about.title.l3")}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5">
            <img src={aboutImg} alt="Classical Ayurvedic medicine preparation at Yuva Ayurvedic Clinic Prayagraj — 18+ years of confidential men's sexual wellness care" className="rounded-2xl aspect-[4/5] object-cover w-full" loading="lazy" decoding="async"/>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="font-serif-display text-2xl sm:text-3xl leading-snug text-foreground/90">
              Yuva Ayurvedic Clinic Private Limited began in Prayagraj in 2008 with a
              conviction that has only grown stronger — that men deserve a trusted,
              completely confidential place to heal sexual health concerns through
              classical Ayurvedic medicine and natural treatments.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-muted-foreground">
              Our founders trained in classical Ayurvedic sciences — with a deep focus on
              vajikarana (virility therapy), rasayana (rejuvenation) and men's
              reproductive health — before returning home to open a specialised clinic
              built around long, private consultations and genuine follow-through. Today,
              over fifty thousand patients across Uttar Pradesh and NRI families abroad
              trust us with their recovery.
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              We see men navigating premature ejaculation, erectile dysfunction,
              night-fall, low sperm count, mardana kamzori and the quieter struggles
              that rarely find space in a modern OPD. Every consultation begins the same
              way — with time, attention, a careful nadi pariksha, and a personalised
              Ayurvedic protocol your body can actually sustain.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-ivory/60">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="overline">What we stand for</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-4 leading-[1] max-w-2xl">Our <span className="italic text-terracotta">philosophy.</span></h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-10">
            {[
              { h: "100% confidential", p: "Private consultation rooms, private records, private pharmacy. Nothing about your visit is ever disclosed — in the waiting area or outside." },
              { h: "Root over symptom", p: "Classical Ayurveda treats the soil, not just the weed. Symptom relief without root-cause work is temporary." },
              { h: "Traceable to the classics", p: "Every rasayana, vajikarana formulation and lifestyle protocol we prescribe can be traced to a classical Ayurvedic text." },
              { h: "Unhurried time", p: "First consultations are 45 minutes. Follow-ups are never less than 20. Anything less dishonours the patient." },
            ].map((v, i) => (
              <Reveal key={v.h} delay={i * 0.08}>
                <div className="border-t border-border pt-6">
                  <h3 className="font-serif-display text-3xl">{v.h}</h3>
                  <p className="mt-3 text-muted-foreground">{v.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <ExploreInDepth
        heading="Speak to the right specialist for your concern"
        subheading="Our senior Ayurvedic physician treats every men's private condition under one roof. Choose the page closest to your concern to read about our specific protocol before you visit."
      />
    </div>
  );
}
